import { RadioGroup, Radio } from "@heroui/react";
import { MonitorIcon, MoonIcon, SunIcon } from "@phosphor-icons/react";
import { useTheme } from "next-themes";

import { DrawerAndModal } from "@/shared/components/common/DrawerAndModal";
import { cloneElement } from "react";
import { ICON_SIZE } from "@/config/constants";
import { useBoundStore } from "@/app/store";
import { useShallow } from "zustand/shallow";
import { Theme } from "@/types/common";

interface ManageThemeProps {
  id?: string;
  externalState?: {
    isOpen: boolean;
    onOpenChange: (open: boolean) => void;
  };
}

export function ManageTheme({ id = "manage-theme", externalState }: ManageThemeProps) {
  const { setTheme } = useTheme();
  const { userId } = useBoundStore(useShallow((state) => ({ userId: state.user.id })));
  const { appSettings, updateSettings } = useBoundStore(
    useShallow((state) => ({ appSettings: state.settings.app, updateSettings: state.settings.updateSettings }))
  );
  const currentTheme = appSettings.theme;
  const icon = currentTheme === "light" ? <SunIcon /> : currentTheme === "dark" ? <MoonIcon /> : <MonitorIcon />;
  const IconElement = cloneElement(icon, { size: ICON_SIZE.md });

  const handleThemeChange = async (newTheme: Theme) => {
    if (!appSettings || !userId) {
      return;
    }

    // Update next-themes
    const updatedSettings = { app: { ...appSettings, theme: newTheme } };
    setTheme(newTheme);
    updateSettings(userId, updatedSettings);
  };

  return (
    <DrawerAndModal id={id} icon={IconElement} title="Select Theme" closeLabel="Close" externalState={externalState}>
      <RadioGroup
        classNames={{ wrapper: "gap-4" }}
        value={currentTheme}
        onValueChange={(value) => handleThemeChange(value as Theme)}
        label="Choose your theme"
      >
        <Radio classNames={{ base: "min-w-full" }} value="light">
          Light
        </Radio>
        <Radio classNames={{ base: "min-w-full" }} value="dark">
          Dark
        </Radio>
        <Radio classNames={{ base: "min-w-full" }} value="system">
          System
        </Radio>
      </RadioGroup>
    </DrawerAndModal>
  );
}
